import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import DynamicForm, { MultiSelectField } from "@/components/DynamicForm";
import DynamicTable from "@/components/DynamicTable";
import GoogleReviewGrid from "@/components/GoogleReviewGrid";
import OutletSheetGrid from "@/components/OutletSheetGrid";
import StockTracker from "@/components/StockTracker";
import { ScrollNavigator } from "@/components/ScrollNavigator";
import { ReleaseNotesDialog } from "@/components/ReleaseNotesDialog";
import { UpdateAvailableBanner } from "@/components/UpdateAvailableBanner";
import { TrashDialog } from "@/components/TrashDialog";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { REGION_ORDER, REGION_STYLE, fmtRM } from "@/lib/report";
import { APP_VERSION } from "@/lib/version";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Download, Eye, Plus, Menu, Upload, FileDown, X, Pencil, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  ["Jan", "January"], ["Feb", "February"], ["Mar", "March"], ["Apr", "April"],
  ["May", "May"], ["Jun", "June"], ["Jul", "July"], ["Aug", "August"],
  ["Sep", "September"], ["Oct", "October"], ["Nov", "November"], ["Dec", "December"],
];
const currentMonthAbbr = () => new Date().toLocaleString("en-US", { month: "short" });
const yy = () => String(new Date().getFullYear()).slice(2);
// Calendar for a new record opens on the month currently selected, not on today's month.
const monthStartDate = (abbr) => {
  const i = MONTHS.findIndex(([a]) => a === abbr);
  return i < 0 ? new Date() : new Date(new Date().getFullYear(), i, 1);
};

const defaultPeriod = () => {
  const d = new Date();
  return d.toLocaleString("en-US", { month: "long" }) + " " + String(d.getFullYear()).slice(2);
};
const defaultPreparedDate = () => new Date().toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric" });

function CustomOptionsPanel() {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dynamic-fields`);
        setFields(data);
        const results = await Promise.all(data.map((r) => axios.get(`${API}/reports/${r.report_id}/custom-options`)));
        const map = {};
        data.forEach((r, i) => { map[r.report_id] = results[i].data; });
        setValues(map);
      } catch (e) { console.error("Load custom options failed", e); }
      setLoading(false);
    })();
  }, []);

  const addValue = async (reportId, fieldKey) => {
    const key = `${reportId}:${fieldKey}`;
    const val = (inputs[key] || "").trim();
    if (!val) return;
    try {
      await axios.post(`${API}/reports/${reportId}/custom-options`, { field_key: fieldKey, value: val });
      setValues((v) => ({ ...v, [reportId]: { ...v[reportId], [fieldKey]: [...(v[reportId]?.[fieldKey] || []), val].sort((a, b) => a.localeCompare(b)) } }));
      setInputs((i) => ({ ...i, [key]: "" }));
      toast.success(`Added "${val}"`);
    } catch (e) { toast.error("Failed to add option"); }
  };

  const removeValue = async (reportId, fieldKey, val) => {
    try {
      await axios.delete(`${API}/reports/${reportId}/custom-options`, { data: { field_key: fieldKey, value: val } });
      setValues((v) => ({ ...v, [reportId]: { ...v[reportId], [fieldKey]: (v[reportId]?.[fieldKey] || []).filter((x) => x !== val) } }));
    } catch (e) { toast.error("Failed to remove option"); }
  };

  if (loading) return <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>;
  if (fields.length === 0) return <p className="text-sm text-slate-400 py-6 text-center">No customizable fields configured.</p>;

  return (
    <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-1">
      {fields.map((r) => (
        <div key={r.report_id} data-testid={`custom-opts-report-${r.report_id}`}>
          <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{r.report_name}</h4>
          <div className="space-y-3">
            {r.fields.map((f) => {
              const key = `${r.report_id}:${f.key}`;
              const list = values[r.report_id]?.[f.key] || [];
              return (
                <div key={f.key} className="rounded-lg border border-slate-200 p-2.5">
                  <div className="text-xs font-semibold text-slate-600 mb-1.5">{f.label}</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {list.length === 0 && <span className="text-xs text-slate-400">No custom entries yet</span>}
                    {list.map((val) => (
                      <span key={val} data-testid={`custom-opt-chip-${r.report_id}-${f.key}-${val}`}
                        className="inline-flex items-center gap-1 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2 py-0.5">
                        {val}
                        <button type="button" onClick={() => removeValue(r.report_id, f.key, val)}
                          data-testid={`custom-opt-remove-${r.report_id}-${f.key}-${val}`}
                          className="text-amber-500 hover:text-red-600 transition-colors">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    <Input value={inputs[key] || ""} placeholder={`Add ${f.label.toLowerCase()}…`}
                      data-testid={`custom-opt-input-${r.report_id}-${f.key}`}
                      onChange={(e) => setInputs((i) => ({ ...i, [key]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addValue(r.report_id, f.key); } }}
                      className="h-8 text-sm" />
                    <Button type="button" size="sm" variant="outline" className="h-8"
                      data-testid={`custom-opt-add-${r.report_id}-${f.key}`}
                      onClick={() => addValue(r.report_id, f.key)}>Add</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function EditableTitle({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const save = () => {
    setEditing(false);
    const v = draft.trim();
    if (v && v !== value) onSave(v);
    else setDraft(value || "");
  };
  if (editing) {
    return (
      <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
        }}
        data-testid="report-title-input" className="text-lg sm:text-xl font-extrabold h-8 py-0" />
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} data-testid="report-title-edit-btn"
      className="group flex items-center gap-1.5 text-lg sm:text-xl font-extrabold text-slate-900 truncate hover:text-amber-600 transition-colors max-w-full">
      <span className="truncate">{value}</span>
      <Pencil className="w-3.5 h-3.5 text-slate-300 group-hover:text-amber-500 shrink-0" />
    </button>
  );
}

export default function Dashboard() {
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [config, setConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [period, setPeriod] = useState(defaultPeriod());
  const [selectedMonth, setSelectedMonth] = useState(currentMonthAbbr());
  const [preparedDate, setPreparedDate] = useState(defaultPreparedDate());
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hiddenReports, setHiddenReports] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashCount, setTrashCount] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState(false);
  const [vendorFilter, setVendorFilter] = useState([]);
  const [photoPrep, setPhotoPrep] = useState(null);
  const [recordMonth, setRecordMonth] = useState(null);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const [releaseNotesBusy, setReleaseNotesBusy] = useState(false);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [searchTarget, setSearchTarget] = useState(null);
  const recordsRef = useRef([]);
  const activeRef = useRef(null);
  const latestVersion = useVersionCheck(APP_VERSION);

  const loadInit = useCallback(async () => {
    setBootError(false);
    setBooting(true);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const [rep, set, trash] = await Promise.all([
          axios.get(`${API}/reports`, { timeout: 20000 }),
          axios.get(`${API}/settings`, { timeout: 20000 }).catch(() => ({ data: { hidden_reports: [], acknowledged_version: null } })),
          axios.get(`${API}/trash`, { timeout: 20000 }).catch(() => ({ data: { count: 0 } })),
        ]);
        const list = Array.isArray(rep.data) ? rep.data : [];
        if (!list.length) {
          if (attempt < 2) { await new Promise((r) => setTimeout(r, 1500)); continue; } // empty -> retry
          setBootError(true); setBooting(false); return;
        }
        const hidden = set.data.hidden_reports || [];
        setReports(list);
        setHiddenReports(hidden);
        setTrashCount(trash.data.count || 0);
        setReleaseNotesOpen(set.data.acknowledged_version !== APP_VERSION);
        const firstVisible = list.find((r) => !hidden.includes(r.id)) || list[0];
        if (firstVisible) setActive(firstVisible.id);
        setBooting(false);
        return;
      } catch (e) {
        if (e?.response?.status === 401) { setBooting(false); return; } // interceptor logs out & redirects
        if (attempt < 2) { await new Promise((r) => setTimeout(r, 1500)); continue; } // retry cold-start
        console.error("Initial load failed", e);
        setBootError(true);
        setBooting(false);
      }
    }
  }, []);

  useEffect(() => { loadInit(); }, [loadInit]);

  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { recordsRef.current = records; }, [records]);

  useEffect(() => {
    if (!searchTarget || active !== searchTarget.report_id || config?.id !== searchTarget.report_id) return undefined;
    let attempt = 0;
    let seekTimer;
    let clearTimer;
    const seek = () => {
      const element = Array.from(document.querySelectorAll("[data-search-record-id]"))
        .find((node) => node.getAttribute("data-search-record-id") === searchTarget.record_id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        clearTimer = window.setTimeout(() => setSearchTarget((current) => (
          current?.record_id === searchTarget.record_id ? null : current
        )), 4500);
        return;
      }
      attempt += 1;
      if (attempt < 35) seekTimer = window.setTimeout(seek, 150);
    };
    seekTimer = window.setTimeout(seek, 120);
    return () => {
      window.clearTimeout(seekTimer);
      window.clearTimeout(clearTimer);
    };
  }, [active, config?.id, records, searchTarget]);

  const loadRecords = useCallback(async (id, notify = false) => {
    const res = await axios.get(`${API}/reports/${id}/records`);
    if (activeRef.current !== id) return;
    const next = Array.isArray(res.data) ? res.data : [];
    const changed = JSON.stringify(next) !== JSON.stringify(recordsRef.current);
    recordsRef.current = next;
    setRecords(next);
    if (notify && changed) {
      toast.success(<span data-testid={`auto-sync-toast-${id}`}>New data synced</span>, {
        id: `auto-sync-${id}`,
        description: <span data-testid={`auto-sync-toast-description-${id}`}>Changes from another device are now visible.</span>,
      });
    }
  }, []);

  const loadConfig = useCallback(async (id) => {
    for (let i = 0; i < 3; i++) {
      try {
        const r = await axios.get(`${API}/reports/${id}/config`, { timeout: 20000 });
        setConfig(r.data);
        return;
      } catch (e) {
        if (e?.response?.status === 401) return;
        if (i < 2) { await new Promise((res) => setTimeout(res, 1500)); continue; }
        console.error("Config load failed", e);
      }
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    setEditing(null);
    setConfig(null);
    setVendorFilter([]);
    loadConfig(active);
    loadRecords(active);
  }, [active, loadConfig, loadRecords]);

  const syncRecords = useCallback(async () => {
    if (!active || active === "google_review" || config?.stock_mode) return;
    try { await loadRecords(active, true); }
    catch (e) { if (e?.response?.status !== 401) console.error("Auto sync failed", e); }
  }, [active, config?.stock_mode, loadRecords]);
  useVisibilityPolling(syncRecords, Boolean(active && active !== "google_review" && !config?.stock_mode), 5000);

  const handleSubmit = async (payload) => {
    const monthForNew = selectedMonth === "Year" ? currentMonthAbbr() : selectedMonth;
    const target = recordMonth || (editing ? (editing.report_month || monthForNew) : monthForNew);
    payload = { ...payload, report_month: target };
    try {
      if (editing) {
        await axios.put(`${API}/reports/${active}/records/${editing.id}`, payload);
        toast.success(target === selectedMonth ? "Record updated" : `Record moved to ${target}`);
        setEditing(null);
      } else {
        await axios.post(`${API}/reports/${active}/records`, payload);
        toast.success(target === selectedMonth ? "Record added" : `Record added to ${target}`);
      }
      setRecordMonth(null);
      loadRecords(active);
      persistDynamicOptions(payload.data);
    } catch (e) {
      toast.error("Failed to save record");
    }
  };

  const handleTitleSave = async (newTitle) => {
    try {
      await axios.put(`${API}/reports/${active}/title`, { title: newTitle });
      setConfig((c) => ({ ...c, title: newTitle }));
      toast.success("Title updated");
    } catch (e) { toast.error("Failed to update title"); }
  };

  const persistDynamicOptions = async (data) => {
    if (!config?.columns) return;
    let added = false;
    for (const c of config.columns) {
      if (!c.dynamic_options) continue;
      const raw = data[c.key];
      const values = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      for (const v of values) {
        const val = String(v).trim();
        if (!val) continue;
        const exists = (c.options || []).some((o) => o.toLowerCase() === val.toLowerCase());
        if (exists) continue;
        try {
          const res = await axios.post(`${API}/reports/${active}/custom-options`, { field_key: c.key, value: val });
          if (res.data?.added) added = true;
        } catch (e) { console.error("Persist custom option failed", e); }
      }
    }
    if (added) loadConfig(active);
  };

  const confirmDelete = async () => {
    try {
      const { data } = await axios.delete(`${API}/reports/${active}/records/${deleting.id}`);
      setTrashCount((count) => count + (data.trash_groups || 1));
      toast.success("Moved to Trash · recoverable for 3 days");
      if (editing?.id === deleting.id) setEditing(null);
      setDeleting(null);
      loadRecords(active);
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const confirmBulkDelete = async () => {
    if (!selectedRecordIds.length) return;
    setBulkDeleteBusy(true);
    try {
      const { data } = await axios.post(`${API}/reports/${active}/records/bulk-delete`, { record_ids: selectedRecordIds });
      const linked = data.linked_deleted ? ` and ${data.linked_deleted} linked record${data.linked_deleted === 1 ? "" : "s"}` : "";
      setTrashCount((count) => count + (data.trash_groups || data.deleted || 0));
      toast.success(`${data.deleted} record${data.deleted === 1 ? "" : "s"} moved to Trash${linked}`);
      if (editing && selectedRecordIds.includes(editing.id)) setEditing(null);
      setSelectedRecordIds([]);
      setBulkDeleteOpen(false);
      loadRecords(active);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete selected records");
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  const META_KEYS = ["id", "report_id", "region", "date_red", "report_month", "created_at", "updated_at", "_id", "carried_from", "carried_from_month"];
  const handleResizeImage = async (record, colKey, index, dims) => {
    const current = (record[colKey] || []).map((it) => (typeof it === "string" ? { path: it } : { ...it }));
    if (!current[index]) return;
    current[index] = { ...current[index], w: dims.w, h: dims.h };
    const data = Object.fromEntries(Object.entries(record).filter(([k]) => !META_KEYS.includes(k)));
    data[colKey] = current;
    try {
      await axios.put(`${API}/reports/${active}/records/${record.id}`,
        { data, region: record.region, date_red: record.date_red, report_month: record.report_month });
      loadRecords(active);
    } catch (e) {
      console.error("Resize save failed", e);
      toast.error("Failed to resize photo");
    }
  };

  const monthParam = () => ((isSheet || isStock || selectedMonth === "Year") ? null : selectedMonth);

  // PDFs are built by a background job: photos are shrunk server-side first, and the browser
  // only ever makes short polling calls, so a photo-heavy report can never hit a gateway timeout.
  const runPdfJob = async (body) => {
    let start;
    try {
      start = await axios.post(`${API}/reports/${active}/pdf_job`, body, { timeout: 60000 });
    } catch (e) {
      if (e?.response?.status !== 404) throw e;
      return buildBlobDirect(body);   // older backend without the job API
    }
    const jobId = start.data.job_id;
    let st = start.data;
    for (let i = 0; i < 1200 && st.state === "running"; i++) {
      if (st.total) setPhotoPrep({ done: st.done, total: st.total, stage: st.stage });
      await new Promise((r) => setTimeout(r, i < 3 ? 600 : 1500));
      try {
        st = (await axios.get(`${API}/reports/pdf_job/${jobId}`, { timeout: 30000 })).data;
      } catch (e) {
        if (e?.response?.status !== 404) throw e;
        setPhotoPrep(null);
        return buildBlobDirect(body);  // job lost (server restarted): fall back to the direct call
      }
    }
    setPhotoPrep(null);
    if (st.state !== "done") throw new Error(st.error || "PDF generation failed");
    const res = await axios.get(`${API}/reports/pdf_job/${jobId}/file`,
      { responseType: "blob", timeout: 120000 });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const buildBlobDirect = async (body) => {
    const res = await axios.post(`${API}/reports/${active}/pdf`, body,
      { responseType: "blob", timeout: 180000 });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const buildBlob = () => runPdfJob(
    { period, prepared_date: preparedDate, prepared_by: "Operation Department", month: monthParam() });

  const buildVendorBlob = () => runPdfJob(
    { period, prepared_date: preparedDate, prepared_by: "Operation Department",
      month: selectedMonth === "Year" ? null : selectedMonth, vendors: vendorFilter });

  const hasAmountSummary = () =>
    config?.grand_total && config?.sum_fields?.some((f) => config.columns.find((c) => c.key === f)?.type === "amount");

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectMonth = (abbr, full) => {
    setSelectedMonth(abbr);
    setPeriod(`${full} ${yy()}`);
    setEditing(null);
    setRecordMonth(null);
  };

  const selectYearly = () => {
    setSelectedMonth("Year");
    setPeriod(`Yearly Total ${new Date().getFullYear()}`);
    setEditing(null);
  };

  const handleSearchResult = (result) => {
    setSearchTarget(result);
    setEditing(null);
    setRecordMonth(null);
    setActive(result.report_id);
    const month = MONTHS.find(([abbr]) => abbr === result.report_month);
    if (month) {
      setSelectedMonth(month[0]);
      setPeriod(`${month[1]} ${yy()}`);
    }
    if (hiddenReports.includes(result.report_id)) {
      const next = hiddenReports.filter((id) => id !== result.report_id);
      setHiddenReports(next);
      axios.put(`${API}/settings`, { hidden_reports: next }).catch(() => {
        toast.error("Opened the record, but couldn't save sidebar visibility");
      });
    }
    setSidebarOpen(false);
  };

  const handlePreview = async () => {
    const hasData = isSheet ? records.length > 0 : filteredRecords.length > 0;
    if (!hasData) return toast.error(isSheet ? "No data to generate — fill in some cells first" : "No records for the selected month");
    setBusy(true);
    try {
      const blob = await buildBlob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewOpen(true);
    } catch (e) { console.error("Preview failed", e); toast.error(e?.response?.status ? `Preview failed (HTTP ${e.response.status})` : "Failed to generate preview"); } finally { setBusy(false); }
  };

  const handleDownload = async () => {
    const hasData = isSheet ? records.length > 0 : filteredRecords.length > 0;
    if (!hasData) return toast.error(isSheet ? "No data to generate — fill in some cells first" : "No records for the selected month");
    setBusy(true);
    try {
      const base = `${config.title.replace(/\s+/g, "_")}_${period.replace(/\s+/g, "_")}`;
      const blob = await buildBlob();
      downloadBlob(blob, `${base}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { console.error("Generate PDF failed", e); toast.error(e?.response?.status ? `Generate PDF failed (HTTP ${e.response.status})` : "Failed to generate PDF"); } finally { setBusy(false); }
  };

  const handleVendorDownload = async () => {
    if (vendorFilter.length === 0) return toast.error("Select at least one vendor first");
    if (vendorFilteredRecords.length === 0) return toast.error("No records for the selected vendor(s)");
    setBusy(true);
    try {
      const base = `${config.title.replace(/\s+/g, "_")}_${period.replace(/\s+/g, "_")}_Vendor`;
      const blob = await buildVendorBlob();
      downloadBlob(blob, `${base}.pdf`);
      toast.success("Vendor PDF downloaded");
    } catch (e) { console.error("Generate Vendor PDF failed", e); toast.error(e?.response?.status ? `Generate Vendor PDF failed (HTTP ${e.response.status})` : "Failed to generate PDF"); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (active && hiddenReports.includes(active)) {
      const first = reports.find((r) => !hiddenReports.includes(r.id));
      if (first) setActive(first.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenReports]);

  useEffect(() => {
    if (config && !config.yearly_total && selectedMonth === "Year") {
      const now = new Date();
      setSelectedMonth(currentMonthAbbr());
      setPeriod(`${now.toLocaleString("en-US", { month: "long" })} ${yy()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const toggleReportVisibility = async (id) => {
    const willHide = !hiddenReports.includes(id);
    const next = willHide ? [...hiddenReports, id] : hiddenReports.filter((x) => x !== id);
    if (next.length >= reports.length) { toast.error("Keep at least one report visible"); return; }
    setHiddenReports(next);
    try {
      await axios.put(`${API}/settings`, { hidden_reports: next });
    } catch (e) {
      console.error("Save settings failed", e);
      toast.error("Failed to save settings");
    }
  };

  const acknowledgeReleaseNotes = async () => {
    setReleaseNotesBusy(true);
    try {
      await axios.put(`${API}/settings/version-ack`, { version: APP_VERSION });
      setReleaseNotesOpen(false);
    } catch (e) {
      console.error("Version acknowledgement failed", e);
      toast.error("确认失败，请稍后再试");
    } finally {
      setReleaseNotesBusy(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error("Please choose a CSV or Excel file"); return; }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const url = `${API}/reports/${active}/import?replace=${replaceExisting}&default_month=${encodeURIComponent(selectedMonth)}`;
      const { data } = await axios.post(url, fd);
      toast.success(`Imported ${data.imported} record(s)${data.replaced ? " (replaced existing)" : ""}`);
      setImportOpen(false); setImportFile(null); setReplaceExisting(false);
      loadRecords(active);
    } catch (e) {
      console.error("Import failed", e);
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImportBusy(false); }
  };

  const isGoogle = config?.id === "google_review";
  const isSheet = !!config?.sheet_mode;
  const isStock = !!config?.stock_mode;
  // Movement Report: Period doubles as the month picker, so a record lands in the chosen month directly.
  const isMonthPeriod = config?.id === "movement_report";
  const effectiveRecordMonth = recordMonth || editing?.report_month
    || (selectedMonth === "Year" ? currentMonthAbbr() : selectedMonth);
  const isYearly = selectedMonth === "Year";
  const filteredRecords = useMemo(
    () => ((isGoogle || isYearly) ? records : records.filter((r) => (r.report_month || "") === selectedMonth)),
    [isGoogle, isYearly, records, selectedMonth],
  );

  const vendorOptions = Array.from(new Set([
    ...(config?.columns?.find((c) => c.key === "vendor")?.options || []),
    ...filteredRecords.map((r) => r.vendor).filter(Boolean),
  ])).sort();
  const vendorFilteredRecords = useMemo(
    () => ((config?.vendor_filter && vendorFilter.length)
      ? filteredRecords.filter((r) => vendorFilter.includes(r.vendor))
      : filteredRecords),
    [config?.vendor_filter, filteredRecords, vendorFilter],
  );

  useEffect(() => {
    const visibleIds = new Set(vendorFilteredRecords.map((record) => record.id));
    setSelectedRecordIds((current) => {
      const next = current.filter((id) => visibleIds.has(id));
      return next.length === current.length ? current : next;
    });
  }, [vendorFilteredRecords]);

  const sumField = config?.sum_fields?.find((f) => (config.columns.find((c) => c.key === f)?.type === "amount"));
  const grandAmount = sumField ? filteredRecords.reduce((s, r) => s + (Number(r[sumField]) || 0), 0) : null;

  const regionCounts = REGION_ORDER.map((rg) => ({
    region: rg, count: filteredRecords.filter((r) => r.region === rg).length,
    amount: sumField ? filteredRecords.filter((r) => r.region === rg).reduce((s, r) => s + (Number(r[sumField]) || 0), 0) : null,
  }));

  const visibleReports = reports.filter((r) => !hiddenReports.includes(r.id));

  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="app-loading">
        <div className="flex items-center gap-3 text-slate-400 text-sm">
          <div className="w-4 h-4 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
          Loading your reports…
        </div>
      </div>
    );
  }
  if (reports.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center" data-testid="app-load-error">
        <p className="text-slate-600 text-sm max-w-sm">Couldn't load your reports. This can happen right after the server wakes up. Please try again.</p>
        <div className="flex gap-3">
          <Button onClick={() => { setBooting(true); loadInit(); }} data-testid="retry-load-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">Retry</Button>
          <Button variant="outline" onClick={() => window.location.reload()} data-testid="reload-page-btn">Reload page</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50/60">
      <ReleaseNotesDialog open={releaseNotesOpen} busy={releaseNotesBusy} onAcknowledge={acknowledgeReleaseNotes} />
      <Sidebar reports={visibleReports} active={active} open={sidebarOpen} onClose={() => setSidebarOpen(false)}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenTrash={() => { setTrashOpen(true); setSidebarOpen(false); }} trashCount={trashCount}
        onSearchResult={handleSearchResult}
        onSelect={(id) => { setActive(id); setSidebarOpen(false); }} />

      <TrashDialog open={trashOpen} onOpenChange={setTrashOpen} onCountChange={setTrashCount}
        onRestored={() => active && loadRecords(active)} />

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="settings-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
            <DialogDescription>Manage sidebar reports and custom dropdown options.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="reports">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="reports" data-testid="settings-tab-reports">Reports</TabsTrigger>
              <TabsTrigger value="custom-options" data-testid="settings-tab-custom-options">Custom Options</TabsTrigger>
            </TabsList>
            <TabsContent value="reports">
              <div className="space-y-1 max-h-[55vh] overflow-y-auto">
                {reports.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-slate-50">
                    <span className="text-sm font-medium text-slate-800">{r.name}</span>
                    <Switch checked={!hiddenReports.includes(r.id)} onCheckedChange={() => toggleReportVisibility(r.id)} data-testid={`toggle-report-${r.id}`} />
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="custom-options">
              <CustomOptionsPanel />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import from CSV / Excel</DialogTitle>
            <DialogDescription>
              Upload a .csv or .xlsx file. The first row with your column titles (e.g. Date, Code, Outlet) is used as the header. Each record's month is read from its Date; rows without a date use the currently selected month ({selectedMonth}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input type="file" accept=".csv,.xlsx,.xlsm" data-testid="import-file"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-white file:font-semibold hover:file:bg-slate-800" />
            <label className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <span className="text-sm font-medium text-slate-700">Replace all existing records</span>
              <Switch checked={replaceExisting} onCheckedChange={setReplaceExisting} data-testid="import-replace" />
            </label>
            <Button onClick={handleImport} disabled={importBusy || !importFile} data-testid="import-submit"
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
              {importBusy ? "Importing…" : "Import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-3 sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden p-2 -ml-1 rounded-md hover:bg-slate-100 text-slate-700" onClick={() => setSidebarOpen(true)} data-testid="menu-toggle" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              {config?.title_editable ? (
                <EditableTitle value={config.title} onSave={handleTitleSave} />
              ) : (
                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 truncate" data-testid="report-title">{config?.name || "…"}</h2>
              )}
              <p className="text-xs text-slate-400 truncate" data-testid="report-subtitle">{config ? (isStock ? "Live inventory and movement history" : `${config.title}_${period}`) : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50/80 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"
              title="Checks for updates every 5 seconds" data-testid="auto-sync-status">
              <RefreshCw className="h-3 w-3" />
              <span className="hidden xl:inline">Auto sync · 5s</span>
            </div>
            {config && !isGoogle && !isSheet && !isStock && (
              <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} disabled={!config} data-testid="import-btn">
                <Upload className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Import</span>
              </Button>
            )}
            {photoPrep && (
              <div className="flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700"
                data-testid="photo-prep-progress">
                <RefreshCw className="h-3 w-3 animate-spin" />
                {photoPrep.stage === "rendering" ? "Building PDF" : `Optimising photos ${photoPrep.done}/${photoPrep.total}`}
              </div>
            )}
            {!isStock && (!isSheet || config?.allow_pdf) && (
            <>
            <Button size="sm" onClick={handlePreview} disabled={busy || !config} data-testid="preview-btn"
              className="relative overflow-hidden group bg-gradient-to-b from-white via-sky-50/40 to-slate-100/90 hover:from-white hover:to-sky-100/60 text-slate-800 font-semibold border border-sky-200/90 hover:border-sky-300/90 ring-1 ring-sky-300/30 shadow-[0_1px_4px_rgba(56,189,248,0.08),inset_0_1px_0_rgba(255,255,255,0.9)] transition-all duration-200">
              <span className="absolute inset-x-0 top-0 h-1/2 rounded-t-md bg-white/60 blur-[1px] pointer-events-none" />
              <span className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent -skew-x-12 transition-transform duration-700 ease-out group-hover:translate-x-[280%] pointer-events-none" />
              <Eye className="w-4 h-4 sm:mr-1.5 relative z-10 text-sky-800" /> <span className="hidden sm:inline relative z-10">Preview</span>
            </Button>
            <Button size="sm" onClick={handleDownload} disabled={busy || !config} data-testid="download-btn"
              className="relative overflow-hidden group bg-gradient-to-b from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 text-slate-900 font-bold ring-1 ring-sky-300/50 shadow-[0_0_16px_rgba(56,189,248,0.3),inset_0_1px_0_rgba(255,255,255,0.5)] transition-all duration-200">
              <span className="absolute inset-x-0 top-0 h-1/2 rounded-t-md bg-white/25 blur-[1px] pointer-events-none" />
              <span className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent -skew-x-12 transition-transform duration-700 ease-out group-hover:translate-x-[280%] pointer-events-none" />
              <Download className="w-4 h-4 sm:mr-1.5 relative z-10" /> <span className="hidden sm:inline relative z-10">Generate PDF</span>
            </Button>
            </>
            )}
            {config?.vendor_filter && (
              <Button size="sm" onClick={handleVendorDownload} disabled={busy || !config || vendorFilter.length === 0}
                data-testid="vendor-pdf-btn" title="Generate a PDF containing only the selected vendor(s)"
                className="relative overflow-hidden group bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 hover:from-slate-800 hover:via-slate-800 hover:to-slate-900 text-white font-bold border border-sky-400/30 ring-1 ring-sky-400/30 shadow-[0_0_14px_rgba(56,189,248,0.2),inset_0_1px_0_rgba(255,255,255,0.25)] transition-all duration-200">
                <span className="absolute inset-x-0 top-0 h-1/2 rounded-t-md bg-white/20 blur-[1px] pointer-events-none" />
                <span className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/35 to-transparent -skew-x-12 transition-transform duration-700 ease-out group-hover:translate-x-[280%] pointer-events-none" />
                <FileDown className="w-4 h-4 sm:mr-1.5 relative z-10 text-sky-200" /> <span className="hidden sm:inline relative z-10">Vendor PDF</span>
              </Button>
            )}
          </div>
        </header>

        <UpdateAvailableBanner currentVersion={APP_VERSION} latestVersion={latestVersion} />

        {!config && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-400" data-testid="content-loading">
            <div className="w-5 h-5 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
            <p className="text-sm">Loading report…</p>
            <Button variant="outline" size="sm" onClick={() => active && loadConfig(active)} data-testid="content-retry">Retry</Button>
          </div>
        )}

        {config && config.id === "google_review" && (
          <main className="flex-1 px-4 sm:px-8 py-4 sm:py-6">
            <GoogleReviewGrid onChanged={() => loadRecords(active)} highlightedId={searchTarget?.report_id === active ? searchTarget.record_id : null} />
          </main>
        )}

        {config && config.sheet_mode && (
          <main className="flex-1 px-4 sm:px-8 py-4 sm:py-6">
            <OutletSheetGrid report={config} onConfigChanged={() => loadConfig(active)} highlightedId={searchTarget?.report_id === active ? searchTarget.record_id : null} />
          </main>
        )}

        {config && config.stock_mode && (
          <main className="flex-1 px-4 sm:px-8 py-4 sm:py-6">
            <StockTracker report={config} />
          </main>
        )}

        {config && config.id !== "google_review" && !config.sheet_mode && !config.stock_mode && (
          <main className="flex-1 px-4 sm:px-8 py-4 sm:py-6 space-y-5">
            {isYearly ? (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-800 font-medium" data-testid="yearly-readonly-note">
                Yearly Total is a view-only summary of all months — switch to a specific month to add or edit records.
              </div>
            ) : (
            /* Add Record form — horizontal, full width at the top */
            <Card className="p-5 shadow-sm border-slate-200">
              <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-amber-500" />{editing ? "Edit Record" : "Add Record"}
                </h3>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full sm:w-auto">
                  <div className="space-y-1.5 w-full sm:w-44">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Period</Label>
                    {isMonthPeriod ? (
                      <select value={selectedMonth} data-testid="select-period-month"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "Year") return selectYearly();
                          const m = MONTHS.find(([abbr]) => abbr === v);
                          if (m) selectMonth(m[0], m[1]);
                        }}
                        className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm outline-none transition-colors hover:border-amber-300 focus:border-amber-400 cursor-pointer">
                        {MONTHS.map(([abbr, full]) => (
                          <option key={abbr} value={abbr}>{`${full} ${yy()}`}</option>
                        ))}
                        {config.yearly_total && <option value="Year">{`Yearly Total ${new Date().getFullYear()}`}</option>}
                      </select>
                    ) : (
                      <Input value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="input-period" placeholder="July 26" className="h-9 w-full sm:w-36" />
                    )}
                    {isMonthPeriod && (
                      <p className="text-[10px] text-slate-400">New records are saved to {selectedMonth === "Year" ? currentMonthAbbr() : selectedMonth}</p>
                    )}
                  </div>
                  {(editing || (recordMonth && recordMonth !== selectedMonth)) && (
                    <div className="space-y-1.5 w-full sm:w-44">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Saved to month</Label>
                      <select value={effectiveRecordMonth} data-testid="select-record-month"
                        onChange={(e) => setRecordMonth(e.target.value)}
                        className="h-9 w-full rounded-md border border-amber-300 bg-amber-50/60 px-3 text-sm font-semibold text-slate-800 shadow-sm outline-none transition-colors focus:border-amber-400 cursor-pointer">
                        {MONTHS.map(([abbr, full]) => (
                          <option key={abbr} value={abbr}>{`${full} ${yy()}`}</option>
                        ))}
                      </select>
                      {effectiveRecordMonth !== selectedMonth && (
                        <p className="text-[10px] font-semibold text-amber-600" data-testid="record-month-hint">
                          This record will move to {effectiveRecordMonth}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prepared Date</Label>
                    <Input value={preparedDate} onChange={(e) => setPreparedDate(e.target.value)} data-testid="input-prepared-date" className="h-9 w-full sm:w-48" />
                  </div>
                </div>
              </div>
              <DynamicForm report={config} editing={editing} onSubmit={handleSubmit} onCancel={() => { setEditing(null); setRecordMonth(null); }} horizontal
                calendarMonth={monthStartDate(selectedMonth)}
                onDateMonth={(abbr) => setRecordMonth(abbr)} />
            </Card>
            )}

            {/* Month selector */}
            <Card className="p-2 shadow-sm border-slate-200 flex items-center gap-1 flex-wrap" data-testid="month-selector">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400 px-2">Month</span>
              {MONTHS.map(([abbr, full]) => (
                <button key={abbr} data-testid={`month-${abbr}`} onClick={() => selectMonth(abbr, full)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${selectedMonth === abbr ? "bg-slate-900 text-white shadow" : "text-slate-500 hover:bg-slate-100"}`}>
                  {abbr}
                </button>
              ))}
              {config.yearly_total && (
                <button data-testid="month-Year" onClick={selectYearly}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold transition ml-1 border ${selectedMonth === "Year" ? "bg-indigo-600 text-white border-indigo-500 shadow" : "text-indigo-600 border-indigo-200 hover:bg-indigo-50"}`}>
                  Yearly Total
                </button>
              )}
            </Card>

            {config.carry_forward && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5" data-testid="carry-forward-hint">
                Unresolved items (Pending / On Hold / In Progress) automatically carry forward to the next calendar month until marked Completed.
              </p>
            )}

            {/* Summary cards */}
            {config.region_group ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {regionCounts.map((s) => (
                  <Card key={s.region} className="p-3.5 border-slate-200 shadow-sm relative overflow-hidden" data-testid={`summary-${s.region}`}>
                    <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: REGION_STYLE[s.region].banner }} />
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{s.region}</p>
                    <p className="text-lg font-extrabold text-slate-900 font-mono-num mt-1">{s.amount != null ? fmtRM(s.amount) : s.count}</p>
                    <p className="text-[11px] text-slate-400">{s.count} record{s.count !== 1 ? "s" : ""}</p>
                  </Card>
                ))}
                <Card className="p-3.5 shadow-sm bg-slate-900 text-white" data-testid="summary-total">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-amber-400">Grand Total</p>
                  <p className="text-lg font-extrabold font-mono-num mt-1">{grandAmount != null ? fmtRM(grandAmount) : filteredRecords.length}</p>
                  <p className="text-[11px] text-slate-400">{filteredRecords.length} record{filteredRecords.length !== 1 ? "s" : ""}</p>
                </Card>
              </div>
            ) : (
              <Card className="p-3.5 shadow-sm bg-slate-900 text-white w-fit min-w-[160px]" data-testid="summary-total">
                <p className="text-[11px] font-bold uppercase tracking-wide text-amber-400">Total Records</p>
                <p className="text-lg font-extrabold font-mono-num mt-1">{filteredRecords.length}</p>
                <p className="text-[11px] text-slate-400">{selectedMonth}</p>
              </Card>
            )}

            {/* Data ledger table */}
            <Card className="p-5 shadow-sm border-slate-200">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <h3 className="font-bold text-slate-900">Data Ledger <span className="text-amber-500">· {selectedMonth === "Year" ? "Yearly Total" : selectedMonth}</span></h3>
                <div className="flex items-center gap-3">
                  {selectedRecordIds.length > 0 && !isYearly && (
                    <div className="flex items-center gap-2" data-testid="bulk-selection-actions">
                      <span className="text-xs font-bold text-sky-700" data-testid="selected-record-count">{selectedRecordIds.length} selected</span>
                      <Button type="button" size="sm" variant="destructive" onClick={() => setBulkDeleteOpen(true)}
                        data-testid="delete-selected-records-button" className="h-8">
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete selected
                      </Button>
                    </div>
                  )}
                  {config.vendor_filter && (
                    <div className="w-56" data-testid="vendor-filter-wrap">
                      <MultiSelectField value={vendorFilter} options={vendorOptions} onChange={setVendorFilter}
                        testid="vendor-filter" placeholder="Filter by vendor…" />
                    </div>
                  )}
                  <span className="text-xs text-slate-400">{config.region_group ? "Grouped by region" : "All records"}</span>
                </div>
              </div>
              <DynamicTable report={config} records={vendorFilteredRecords} editingId={editing?.id} onEdit={setEditing} onDelete={setDeleting}
                onResizeImage={handleResizeImage} selectionEnabled={!isYearly} readOnly={isYearly}
                selectedIds={selectedRecordIds} onSelectionChange={setSelectedRecordIds}
                highlightedId={searchTarget?.report_id === active ? searchTarget.record_id : null} />
            </Card>
          </main>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-7xl w-[96vw] h-[92vh] flex flex-col p-0" data-testid="preview-dialog">
          <DialogHeader className="px-5 py-3 border-b flex-row items-center justify-between space-y-0">
            <div>
              <DialogTitle>{config?.title}_{period}</DialogTitle>
              <DialogDescription className="text-xs">PDF preview — auto-fitted to one page</DialogDescription>
            </div>
            <Button onClick={handleDownload} disabled={busy} size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold mr-6">
              <Download className="w-4 h-4 mr-1.5" /> Download
            </Button>
          </DialogHeader>
          {previewUrl && <iframe title="preview" src={previewUrl} className="flex-1 w-full" data-testid="preview-iframe" />}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="delete-dialog-title">Move this record to Trash?</AlertDialogTitle>
            <AlertDialogDescription data-testid="delete-dialog-description">
              {active === "rmu_pending"
                ? "This record and any auto-linked RMU Report / Costing Summary records will move together. You can restore the group within 3 days."
                : "You can restore this record from Trash within 3 days."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Move to Trash</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !open && !bulkDeleteBusy && setBulkDeleteOpen(false)}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="bulk-delete-dialog-title">Move {selectedRecordIds.length} selected records to Trash?</AlertDialogTitle>
            <AlertDialogDescription data-testid="bulk-delete-dialog-description">
              {active === "rmu_pending"
                ? "Auto-linked RMU Report and Costing Summary records will move with their source. Restore each group within 3 days; manual records are unaffected."
                : "Only the selected records will move to Trash. You can restore them within 3 days."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteBusy} data-testid="cancel-bulk-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={bulkDeleteBusy} onClick={confirmBulkDelete}
              className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete-button">
              {bulkDeleteBusy ? "Moving…" : "Move to Trash"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {!isStock && <ScrollNavigator />}
    </div>
  );
}
